import {Clock3, Plus, Search, TriangleAlert} from 'lucide-react';
import {
  Badge,
  Button,
  InputGroup,
  InputGroupAddon,
  InputGroupInput
} from '@kroll-resolver-crisp/ui-components-react';
import type {WorkflowSummary} from '../../api/workflows';

const GROUPS = [
  {status: 'active', title: 'Active'},
  {status: 'draft', title: 'Drafts'},
  {status: 'archived', title: 'Archived'}
] as const;

function subtitle(workflow: WorkflowSummary) {
  if (workflow.status === 'draft') {
    return workflow.proposal_id ? 'Awaiting first approval' : 'Not configured';
  }

  return workflow.status === 'archived' ? 'Not served' : null;
}

export function WorkflowRail({
  workflows,
  selectedId,
  query,
  onQuery,
  onPick,
  onCreate,
  unsent
}: {
  readonly workflows: WorkflowSummary[];
  readonly selectedId: string;
  readonly query: string;
  readonly onQuery: (value: string) => void;
  readonly onPick: (id: string) => void;
  readonly onCreate?: (() => void) | undefined;
  readonly unsent: Record<string, number>;
}) {
  const needle = query.toLowerCase();
  const matches = workflows.filter((w) => `${w.name} ${w.key}`.toLowerCase().includes(needle));
  const groups = GROUPS.map((group) => ({
    ...group,
    rows: matches.filter((w) => w.status === group.status)
  })).filter((group) => group.rows.length > 0);

  return (
    <aside
      aria-label="Risk guidance"
      className="border-border bg-card flex w-full shrink-0 flex-col border-b md:w-80 md:border-r md:border-b-0">
      {onCreate ? (
        <div className="border-border border-b p-2">
          <Button size="sm" className="w-full" onClick={onCreate}>
            <Plus aria-hidden className="size-4" />
            Create guidance
          </Button>
        </div>
      ) : null}
      <div className="border-border border-b p-2">
        <InputGroup>
          <InputGroupAddon>
            <Search aria-hidden className="size-4" />
          </InputGroupAddon>
          <InputGroupInput
            aria-label="Find guidance"
            placeholder="Find guidance"
            value={query}
            onChange={(e) => {
              onQuery(e.target.value);
            }}
          />
        </InputGroup>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto pb-3">
        {groups.map((group) => (
          <div key={group.status}>
            <div className="text-muted-foreground flex justify-between px-3 pt-3.5 pb-1 text-[10px] font-semibold tracking-widest uppercase">
              <span>{group.title}</span>
              <span>{group.rows.length}</span>
            </div>
            {group.rows.map((w) => {
              const active = w.id === selectedId;
              const pending = unsent[w.id] ?? 0;
              const line = subtitle(w);
              return (
                <button
                  key={w.id}
                  type="button"
                  aria-current={active ? 'true' : undefined}
                  className={`hover:bg-muted focus-visible:ring-ring flex w-full min-w-0 flex-col gap-0.5 border-l-2 px-3 py-2 text-left focus-visible:ring-2 focus-visible:outline-none ${active ? 'bg-accent border-primary' : 'border-transparent'}`}
                  onClick={() => {
                    onPick(w.id);
                  }}>
                  <span className="flex min-w-0 items-center gap-1.5">
                    <span className="min-w-0 flex-1 truncate text-sm font-medium">{w.name}</span>
                    {w.proposal_id ? (
                      <Badge variant="secondary">
                        <Clock3 aria-hidden className="size-3" />
                        In review
                      </Badge>
                    ) : null}
                    {w.taxonomy_outdated ? (
                      <Badge variant="outline" className="text-destructive">
                        <TriangleAlert aria-hidden className="size-3" />
                        Outdated
                      </Badge>
                    ) : null}
                  </span>
                  <span className="text-muted-foreground truncate text-xs">
                    {line ?? <code className="font-mono">{w.key}</code>}
                    {pending > 0 ? (
                      <>
                        {' · '}
                        <span className="text-primary">{pending} unsent</span>
                      </>
                    ) : null}
                  </span>
                </button>
              );
            })}
          </div>
        ))}
        {groups.length === 0 ? (
          <p className="text-muted-foreground px-4 py-2 text-xs">No guidance matches.</p>
        ) : null}
      </div>
    </aside>
  );
}
